import { vec2 } from './geometry.js';
const WALL_GLYPH = '#';
const FLOOR_GLYPH = '.';
/**
 * The eight steps, clockwise from north, orthogonals and diagonals alternating.
 *
 * The order is part of the contract: A* and Dijkstra break ties by insertion
 * order, so reshuffling this list would change which of two equal-cost paths a
 * replay reproduces.
 */
const STEPS = [
    { x: 0, y: -1 },
    { x: 1, y: -1 },
    { x: 1, y: 0 },
    { x: 1, y: 1 },
    { x: 0, y: 1 },
    { x: -1, y: 1 },
    { x: -1, y: 0 },
    { x: -1, y: -1 },
];
const assertDimension = (name, value) => {
    if (!Number.isInteger(value) || value <= 0) {
        throw new RangeError(`Grid ${name} must be a positive integer, received ${value}`);
    }
};
/**
 * A grid of the given size. Without `cells` everything is walkable — the caller
 * carves the walls.
 */
export const createGrid = (width, height, cells) => {
    assertDimension('width', width);
    assertDimension('height', height);
    if (cells !== undefined && cells.length !== width * height) {
        throw new RangeError(`Grid ${width}×${height} needs ${width * height} cells, received ${cells.length}`);
    }
    // Copied, never aliased: a map that can change under the pathfinder is a
    // desync waiting to happen.
    return { width, height, cells: cells === undefined ? new Array(width * height).fill(true) : [...cells] };
};
/**
 * Reads a hand-drawn map: `#` is solid, `.` is walkable. Anything else throws,
 * because a typo in a level fixture must not quietly become floor.
 */
export const createGridFromRows = (rows) => {
    const height = rows.length;
    const width = rows[0]?.length ?? 0;
    if (height === 0 || width === 0) {
        throw new RangeError('Grid rows must describe at least one cell');
    }
    const cells = [];
    for (const row of rows) {
        if (row.length !== width) {
            throw new RangeError(`Grid rows must all be ${width} wide, received one of ${row.length}`);
        }
        for (const glyph of row) {
            if (glyph !== WALL_GLYPH && glyph !== FLOOR_GLYPH) {
                throw new RangeError(`Grid rows accept '${FLOOR_GLYPH}' and '${WALL_GLYPH}', received '${glyph}'`);
            }
            cells.push(glyph === FLOOR_GLYPH);
        }
    }
    return createGrid(width, height, cells);
};
/** The inverse of `createGridFromRows`. Exists so a failing map assertion is readable. */
export const formatGrid = (grid) => Array.from({ length: grid.height }, (_unused, y) => Array.from({ length: grid.width }, (_alsoUnused, x) => grid.cells[y * grid.width + x] === true ? FLOOR_GLYPH : WALL_GLYPH).join(''));
/** `true` when the position falls inside the map. Fractional positions count as their cell. */
export const inBounds = (grid, position) => {
    const x = Math.floor(position.x);
    const y = Math.floor(position.y);
    return x >= 0 && y >= 0 && x < grid.width && y < grid.height;
};
/** Row-major index of the cell containing `position`, or `-1` when it is off the map. */
export const cellIndex = (grid, position) => inBounds(grid, position) ? Math.floor(position.y) * grid.width + Math.floor(position.x) : -1;
/** The inverse of `cellIndex`, for indices the caller already knows are on the map. */
export const cellAt = (grid, index) => vec2(index % grid.width, Math.floor(index / grid.width));
/** `true` when an entity may stand here. Off the map is rock, not an error. */
export const isWalkable = (grid, position) => {
    const index = cellIndex(grid, position);
    return index >= 0 && grid.cells[index] === true;
};
/**
 * The cells reachable in one step from `cell` — the movement rule itself.
 *
 * Eight-way, but a diagonal is only offered when both cells flanking it are
 * open, so nothing ever squeezes through the seam between two rocks.
 */
export const walkableNeighbours = (grid, cell) => {
    if (!isWalkable(grid, cell))
        return [];
    const x = Math.floor(cell.x);
    const y = Math.floor(cell.y);
    const neighbours = [];
    for (const step of STEPS) {
        const candidate = vec2(x + step.x, y + step.y);
        if (!isWalkable(grid, candidate))
            continue;
        const isDiagonal = step.x !== 0 && step.y !== 0;
        if (isDiagonal && !(isWalkable(grid, vec2(x + step.x, y)) && isWalkable(grid, vec2(x, y + step.y)))) {
            continue;
        }
        neighbours.push(candidate);
    }
    return neighbours;
};
/**
 * FNV-1a over the shape and the cells: a cheap 32-bit checksum of a map.
 *
 * Server and client each generate the dungeon from the seed rather than shipping
 * it; this is how they prove they generated the same one — and how the test
 * suite pins determinism without diffing thousands of booleans.
 */
export const hashGrid = (grid) => {
    const FNV_OFFSET_BASIS = 0x811c9dc5;
    const FNV_PRIME = 0x0100_0193;
    let hash = FNV_OFFSET_BASIS;
    const mix = (byte) => {
        hash = Math.imul(hash ^ byte, FNV_PRIME);
    };
    mix(grid.width & 0xff);
    mix((grid.width >>> 8) & 0xff);
    mix(grid.height & 0xff);
    mix((grid.height >>> 8) & 0xff);
    for (const cell of grid.cells)
        mix(cell ? 1 : 0);
    return hash >>> 0;
};
//# sourceMappingURL=grid.js.map